/**
 * Field components for schema-driven lead forms. Styling matches the existing
 * site forms. Labels are always a single string child — adjacent text nodes
 * merge in the react-snap snapshot and break hydration (React #418).
 */
import { useRef } from "react";
import { Check, FileText, Upload, X } from "lucide-react";
import { UPLOAD_ACCEPT, UPLOAD_MAX_BYTES, UPLOAD_MAX_FILES, UPLOAD_MIME_TYPES } from "./submit";

export const INPUT_CLASS =
  "w-full px-4 py-3 rounded-xl border bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all";
const LABEL_CLASS = "block text-sm font-semibold mb-1.5 text-brand-black";

const AUTOCOMPLETE = {
  email: "email",
  tel: "tel",
};

function borderClass(error) {
  return error ? "border-red-400" : "border-brand-light-grey";
}

function labelText(field) {
  return field.required ? `${field.label} *` : field.label;
}

function FieldShell({ field, id, error, children, asFieldset = false }) {
  const errorId = `${id}-error`;
  const help = field.help ? <p className="text-xs text-brand-grey mt-1">{field.help}</p> : null;
  const err = error ? (
    <p id={errorId} className="text-xs text-red-600 mt-1" role="alert" data-testid={`${id}-error`}>
      {error}
    </p>
  ) : null;
  if (asFieldset) {
    return (
      <fieldset>
        <legend className={LABEL_CLASS}>{labelText(field)}</legend>
        {children}
        {help}
        {err}
      </fieldset>
    );
  }
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {labelText(field)}
      </label>
      {children}
      {help}
      {err}
    </div>
  );
}

function aria(id, error, required) {
  return {
    "aria-invalid": error ? "true" : undefined,
    "aria-describedby": error ? `${id}-error` : undefined,
    "aria-required": required ? "true" : undefined,
  };
}

function TextField({ field, id, value, error, onChange }) {
  const type = field.type === "currency" ? "text" : field.type;
  const input = (
    <input
      id={id}
      name={field.name}
      type={type === "number" ? "number" : type}
      inputMode={field.type === "currency" || field.type === "number" ? "decimal" : undefined}
      value={value ?? ""}
      placeholder={field.placeholder}
      autoComplete={field.autoComplete || AUTOCOMPLETE[field.type]}
      onChange={(e) => onChange(e.target.value)}
      className={`${INPUT_CLASS} ${borderClass(error)} ${field.type === "currency" ? "pl-8" : ""}`}
      data-testid={`lf-${field.name}`}
      {...aria(id, error, field.required)}
    />
  );
  return (
    <FieldShell field={field} id={id} error={error}>
      {field.type === "currency" ? (
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-brand-grey">£</span>
          {input}
        </div>
      ) : (
        input
      )}
    </FieldShell>
  );
}

function TextareaField({ field, id, value, error, onChange }) {
  return (
    <FieldShell field={field} id={id} error={error}>
      <textarea
        id={id}
        name={field.name}
        rows={field.rows || 4}
        value={value ?? ""}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        onChange={(e) => onChange(e.target.value)}
        className={`${INPUT_CLASS} ${borderClass(error)} resize-none`}
        data-testid={`lf-${field.name}`}
        {...aria(id, error, field.required)}
      />
    </FieldShell>
  );
}

function SelectField({ field, id, value, error, onChange }) {
  return (
    <FieldShell field={field} id={id} error={error}>
      <select
        id={id}
        name={field.name}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`${INPUT_CLASS} ${borderClass(error)} appearance-none`}
        data-testid={`lf-${field.name}`}
        {...aria(id, error, field.required)}
      >
        <option value="">{field.placeholder || "Please select"}</option>
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

function OptionButton({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm transition-all ${
        selected
          ? "bg-brand-aqua-light border-brand-aqua text-brand-black font-semibold"
          : "bg-white border-brand-light-grey text-brand-grey hover:border-brand-aqua/60"
      }`}
      data-testid={testId}
    >
      {selected && <Check size={14} />}
      {children}
    </button>
  );
}

/** Single choice shown as pill buttons (good for short option lists). */
function RadioField({ field, id, value, error, onChange }) {
  return (
    <FieldShell field={field} id={id} error={error} asFieldset>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {field.options.map((o) => (
          <OptionButton
            key={o.value}
            selected={value === o.value}
            onClick={() => onChange(value === o.value && !field.required ? "" : o.value)}
            testId={`lf-${field.name}-${o.value}`}
          >
            {o.label}
          </OptionButton>
        ))}
      </div>
    </FieldShell>
  );
}

/** Multiple choice pills. */
function ChipsField({ field, id, value, error, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <FieldShell field={field} id={id} error={error} asFieldset>
      <div className="flex flex-wrap gap-2">
        {field.options.map((o) => (
          <OptionButton
            key={o.value}
            selected={selected.includes(o.value)}
            onClick={() => toggle(o.value)}
            testId={`lf-${field.name}-${o.value}`}
          >
            {o.label}
          </OptionButton>
        ))}
      </div>
    </FieldShell>
  );
}

function CheckboxField({ field, id, value, error, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-2.5 cursor-pointer">
        <input
          id={id}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-brand-light-grey text-brand-aqua focus:ring-brand-aqua/30"
          data-testid={`lf-${field.name}`}
          {...aria(id, error, field.required)}
        />
        <span className="text-sm text-brand-black leading-relaxed">{labelText(field)}</span>
      </label>
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-600 mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function fileProblem(file) {
  if (!UPLOAD_MIME_TYPES.includes(file.type)) return `${file.name}: please use PDF, Word, JPG or PNG`;
  if (file.size > UPLOAD_MAX_BYTES) return `${file.name}: files must be 10MB or smaller`;
  return null;
}

function FileField({ field, id, value, error, onChange, onReject }) {
  const inputRef = useRef(null);
  const files = Array.isArray(value) ? value : [];
  const max = field.maxFiles || UPLOAD_MAX_FILES;
  const add = (list) => {
    const incoming = Array.from(list || []);
    const problems = incoming.map(fileProblem).filter(Boolean);
    const ok = incoming.filter((f) => !fileProblem(f));
    const merged = [...files, ...ok].slice(0, max);
    if (files.length + ok.length > max) problems.push(`You can upload up to ${max} files`);
    onChange(merged);
    onReject(problems.join(". "));
  };
  return (
    <FieldShell field={field} id={id} error={error} asFieldset>
      <div
        className={`rounded-xl border-2 border-dashed ${error ? "border-red-400" : "border-brand-light-grey"} bg-white p-5 text-center`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          add(e.dataTransfer.files);
        }}
      >
        <Upload size={20} className="mx-auto text-brand-aqua mb-2" />
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          className="text-sm font-semibold text-brand-aqua-dark hover:underline"
          data-testid={`lf-${field.name}-choose`}
        >
          Choose files
        </button>
        <p className="text-xs text-brand-grey mt-1">{field.placeholder || "PDF, Word, JPG or PNG · up to 10MB each"}</p>
        <input
          ref={inputRef}
          id={id}
          type="file"
          multiple={max > 1}
          accept={UPLOAD_ACCEPT}
          className="sr-only"
          onChange={(e) => {
            add(e.target.files);
            e.target.value = "";
          }}
          data-testid={`lf-${field.name}`}
        />
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${f.size}-${f.lastModified}`}
              className="flex items-center justify-between gap-2 text-sm bg-brand-aqua-light/40 rounded-lg px-3 py-2"
            >
              <span className="flex items-center gap-2 truncate">
                <FileText size={14} className="shrink-0" />
                <span className="truncate">{f.name}</span>
              </span>
              <button
                type="button"
                aria-label={`Remove ${f.name}`}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="text-brand-grey hover:text-red-600"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </FieldShell>
  );
}

const COMPONENTS = {
  text: TextField,
  email: TextField,
  tel: TextField,
  number: TextField,
  currency: TextField,
  textarea: TextareaField,
  select: SelectField,
  radio: RadioField,
  chips: ChipsField,
  checkbox: CheckboxField,
  file: FileField,
};

export function Field(props) {
  const { field } = props;
  if (field.type === "hidden") return null;
  if (typeof field.render === "function") return field.render(props);
  const Component = COMPONENTS[field.type] || TextField;
  return <Component {...props} />;
}
